import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, Plus, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { SchemaProperty } from '@/services/schemaApi';

export type PropertyType = 'string' | 'number' | 'boolean' | 'array' | 'object';

export interface PropertyConfig {
  name: string;
  type: PropertyType;
  description?: string;
  required: boolean;
  // String constraints
  enum?: string[];
  pattern?: string;
  // Number constraints
  minimum?: number;
  maximum?: number;
  // Array constraints
  items?: SchemaProperty;
  // Object constraints
  properties?: Record<string, SchemaProperty>;
  nestedRequired?: string[];
}

interface PropertyEditorProps {
  property: PropertyConfig;
  onChange: (property: PropertyConfig) => void;
  onDelete: () => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  level?: number;
}

export function PropertyEditor({
  property,
  onChange,
  onDelete,
  isExpanded = false,
  onToggleExpand,
  level = 0,
}: PropertyEditorProps) {
  const [enumInput, setEnumInput] = useState('');

  const updateProperty = (updates: Partial<PropertyConfig>) => {
    onChange({ ...property, ...updates });
  };

  const handleAddEnumValue = () => {
    if (!enumInput.trim()) return;
    const newEnum = [...(property.enum || []), enumInput.trim()];
    updateProperty({ enum: newEnum });
    setEnumInput('');
  };

  const handleRemoveEnumValue = (index: number) => {
    const newEnum = (property.enum || []).filter((_, i) => i !== index);
    updateProperty({ enum: newEnum.length > 0 ? newEnum : undefined });
  };

  const handleTypeChange = (newType: PropertyType) => {
    // Reset type-specific fields when changing type
    const updates: Partial<PropertyConfig> = { type: newType };

    // Clear constraints that don't apply to new type
    if (newType !== 'string') {
      updates.enum = undefined;
      updates.pattern = undefined;
    }
    if (newType !== 'number') {
      updates.minimum = undefined;
      updates.maximum = undefined;
    }
    if (newType !== 'array') {
      updates.items = undefined;
    }
    if (newType !== 'object') {
      updates.properties = undefined;
      updates.nestedRequired = undefined;
    }

    // Set defaults for new type
    if (newType === 'array') {
      updates.items = { type: 'string' };
    }
    if (newType === 'object') {
      updates.properties = {};
      updates.nestedRequired = [];
    }

    updateProperty(updates);
  };

  return (
    <div
      className="border rounded-lg bg-card"
      style={{ marginLeft: level > 0 ? `${level * 16}px` : 0 }}
    >
      <Collapsible open={isExpanded} onOpenChange={onToggleExpand}>
        <div className="flex items-center gap-2 p-3">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>

          <Input
            value={property.name}
            onChange={(e) => updateProperty({ name: e.target.value })}
            placeholder="property_name"
            className="flex-1 h-8 font-mono text-sm"
          />

          <Select value={property.type} onValueChange={handleTypeChange}>
            <SelectTrigger className="w-28 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="string">string</SelectItem>
              <SelectItem value="number">number</SelectItem>
              <SelectItem value="boolean">boolean</SelectItem>
              <SelectItem value="array">array</SelectItem>
              <SelectItem value="object">object</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1">
            <Switch
              checked={property.required}
              onCheckedChange={(checked) => updateProperty({ required: checked })}
              className="h-5"
            />
            <span className="text-xs text-muted-foreground">Required</span>
          </div>

          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>

        <CollapsibleContent>
          <div className="px-3 pb-3 pt-1 border-t space-y-4">
            {/* Description */}
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={property.description || ''}
                onChange={(e) => updateProperty({ description: e.target.value || undefined })}
                placeholder="Describe this property..."
                rows={2}
                className="text-sm"
              />
            </div>

            {/* String-specific options */}
            {property.type === 'string' && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs">Enum Values (optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={enumInput}
                      onChange={(e) => setEnumInput(e.target.value)}
                      placeholder="Add enum value..."
                      className="h-8 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddEnumValue();
                        }
                      }}
                    />
                    <Button size="sm" variant="outline" onClick={handleAddEnumValue}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  {property.enum && property.enum.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {property.enum.map((value, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="cursor-pointer hover:bg-destructive/20"
                          onClick={() => handleRemoveEnumValue(index)}
                        >
                          {value}
                          <span className="ml-1 text-muted-foreground">×</span>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Pattern (regex)</Label>
                  <Input
                    value={property.pattern || ''}
                    onChange={(e) => updateProperty({ pattern: e.target.value || undefined })}
                    placeholder="^[A-Z]+$"
                    className="h-8 text-sm font-mono"
                  />
                </div>
              </>
            )}

            {/* Number-specific options */}
            {property.type === 'number' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Minimum</Label>
                  <Input
                    type="number"
                    value={property.minimum ?? ''}
                    onChange={(e) =>
                      updateProperty({
                        minimum: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    placeholder="0"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Maximum</Label>
                  <Input
                    type="number"
                    value={property.maximum ?? ''}
                    onChange={(e) =>
                      updateProperty({
                        maximum: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    placeholder="100"
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            )}

            {/* Array-specific options */}
            {property.type === 'array' && (
              <div className="space-y-2">
                <Label className="text-xs">Item Type</Label>
                <Select
                  value={property.items?.type || 'string'}
                  onValueChange={(value) =>
                    updateProperty({
                      items: { type: value as PropertyType },
                    })
                  }
                >
                  <SelectTrigger className="w-full h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">string</SelectItem>
                    <SelectItem value="number">number</SelectItem>
                    <SelectItem value="boolean">boolean</SelectItem>
                    <SelectItem value="object">object</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Object type shows nested properties */}
            {property.type === 'object' && (
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Nested Properties</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Edit nested properties in the schema preview or JSON editor.
                </p>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export default PropertyEditor;
